/**
 * API client for the internal IA-fit review (IA staff only).
 *
 * Authentication: a Google ID token (obtained via Google sign-in on the
 * /internal page) is sent as a Bearer token; the backend verifies it and
 * checks the account against the IA allowlist.
 */
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** OAuth 2.0 Web client ID — must match backend GOOGLE_OAUTH_CLIENT_ID. */
export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const TOKEN_KEY = 'ia_internal_token';

export const getAdminToken = (): string | null => sessionStorage.getItem(TOKEN_KEY);

export const setAdminToken = (token: string | null): void => {
  if (token) {
    sessionStorage.setItem(TOKEN_KEY, token);
  } else {
    sessionStorage.removeItem(TOKEN_KEY);
  }
};

export interface CategoryScore {
  score: number;
  max_score: number;
  reasoning: string;
  display_name: string;
}

export interface AnalysisBlock {
  overall_analysis?: string;
  category_scores?: Record<string, CategoryScore>;
  key_strengths?: string[];
  key_weaknesses?: string[];
  key_concerns?: string[];
  recommendations?: string[];
  next_steps?: string[];
  ia_fit_score?: number;
  overall_score?: number;
}

export interface PitchSummary {
  id: string;
  created_at: string | null;
  filename: string | null;
  contact_email: string | null;
  country: string | null;
  industry: string | null;
  stage: string | null;
  general_score: number | null;
  ia_fit_score: number | null;
  is_good_fit: boolean;
}

export interface PitchDetail extends PitchSummary {
  analyzed_at: string | null;
  word_count: number | null;
  general_analysis: AnalysisBlock;
  ia_fit_analysis: AnalysisBlock;
  full_text: string;
}

const adminClient = axios.create({
  baseURL: `${API_BASE_URL}/api/admin`,
  timeout: 30000,
});

adminClient.interceptors.request.use((config) => {
  const token = getAdminToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const whoami = async (): Promise<string> => {
  const { data } = await adminClient.get<{ email: string }>('/me');
  return data.email;
};

export const listPitches = async (params: {
  limit?: number;
  offset?: number;
  min_ia_fit?: number;
  order_by?: 'created_at' | 'ia_fit_score' | 'general_score';
} = {}): Promise<{ total: number; items: PitchSummary[] }> => {
  const { data } = await adminClient.get('/pitches', { params });
  return data;
};

export const getPitch = async (id: string): Promise<PitchDetail> => {
  const { data } = await adminClient.get(`/pitches/${id}`);
  return data;
};
